import { Component, EventEmitter, OnChanges, OnInit, Output, input, model, output } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Editor, Toolbar } from 'ngx-editor';
import { Subject } from 'rxjs';
import { StateCounty } from 'src/app/models/stateCounty.model';
import { Watershed } from 'src/app/models/watershed.model';
import { ReportService } from 'src/app/services/report.service';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { ReportForest } from 'src/app/models/reportForest.model';
import { APP_CONSTANTS } from 'src/app/shared/app.constants';
import { AutoSaveComponent } from '../auto-save/auto-save.component';
import { NAFilterPipe } from 'src/pipes/naFilter.pipe';

@Component({
    selector: 'app-burned-area',
    templateUrl: './burned-area.component.html',
    styleUrls: ['./burned-area.component.scss'],
    standalone: false
})

export class BurnedAreaComponent extends AutoSaveComponent implements OnChanges, OnInit {

  faWarning = faWarning;
  faCircleXmark = faCircleXmark as IconProp;

  im = new Inputmask({
    groupSeparator: ',',
    prefix: '$',
    allowMinus: false
  });

  optional = input(false);
  simple = input(false);

  editor1!: Editor;
  editor2!: Editor;
  editor3!: Editor;
  editor4!: Editor;
  toolbar: Toolbar = APP_CONSTANTS.EDITOR_TOOLBAR;
  html = "<p>Hello</p>"

  report = input.required<any>();
  nav = model.required<string>();
  status = output<number | undefined>();
  @Output() loaded = new EventEmitter();
  errorList: string[] = [];
  forests: ReportForest[] = []
  counties: string[] = [];
  rangerDistricts: string[] = [];
  states: string[] = [];
  saveSubject = new Subject<any>();

  streamTypes = [
    "Perennial",
    "Intermittent",
    "Ephemeral",
    "Canal/Ditch/Pipeline",
  ]

  fieldDict: any = {
    "Perennial": "stream_miles_perennial",
    "Intermittent": "stream_miles_intermittent",
    "Ephemeral": "stream_miles_ephemeral",
    "Canal/Ditch/Pipeline": "stream_miles_canal",
    "1 - Basic Custodial Care (Closed)": "roads_miles_basic",
    "2 - High Clearance Vehicles": "roads_miles_clearance",
    "3 - Suitable For Passenger Cars": "roads_miles_passenger",
    "4 - Moderate Degree of User Comfort": "roads_miles_moderate",
    "5 - High Degree of User Comfort": "roads_miles_high",
    "Unknown or Decommissioned": "roads_miles_unknown",
    "other_roads": "other_roads",
    "Motorized": "transport_miles_motorized",
    "Non-motorized": "transport_miles_non",
    "Snow Trails": "transport_miles_snow",
  }

  maintenanceLevels = [
    "1 - Basic Custodial Care (Closed)",
    "2 - High Clearance Vehicles",
    "3 - Suitable For Passenger Cars",
    "4 - Moderate Degree of User Comfort",
    "5 - High Degree of User Comfort",
    "Unknown or Decommissioned",
  ]

  trailTypes = [
    "Motorized",
    "Non-motorized",
    "Snow Trails",
  ]

  form = this.formBuilder.group({
    baer_name: [""],
    fire_number: [""],
    job_code: ["", Validators.maxLength(20)],
    fire_discovery_date: [null] as unknown as Date[],
    containment_date_est: [null] as unknown as Date[],
    containment_date: [null] as unknown as Date[],
    percent_contained: [null],

    subwatershed: this.formBuilder.array([]),
    acresBurned: this.formBuilder.array([]),
    streams: this.formBuilder.array([]),
    maintenance: this.formBuilder.array([]),
    trails: this.formBuilder.array([]),

    vegetation_types: ["", Validators.maxLength(6000)],
    dominant_soils: ["", Validators.maxLength(6000)],
    geologic_types: ["", Validators.maxLength(6000)],
    other_roads: ["", Validators.maxLength(6000)],
  });
  get subwatershed() {
    return this.form.get("subwatershed") as FormArray;
  }
  get acresBurned() {
    return this.form.get("acresBurned") as FormArray;
  }
  get streams() {
    return this.form.get("streams") as FormArray;
  }
  get maintenance() {
    return this.form.get("maintenance") as FormArray;
  }
  get trails() {
    return this.form.get("trails") as FormArray;
  }

  constructor(
    public dialog: MatDialog,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    public readonly reportService: ReportService,
    private readonly toast: ConfirmationToastComponent,
    private readonly naFilter: NAFilterPipe,
  ) {
    super()
  }

  ngOnInit(): void {
    this.status.emit(undefined);
    this.editor1 = new Editor();
    this.editor2 = new Editor();
    this.editor3 = new Editor();
    this.editor4 = new Editor();
    this.forests = this.report().forests
    this.counties = this.report().stateCounties.filter((county: StateCounty) => county?.county_name).map((county: StateCounty) => county?.county_name)
    this.states = this.report().stateCounties.filter((state: StateCounty) => state?.state_name != null).map((state: StateCounty) => state?.state_name)
    this.states = [...new Set(this.states)]; //remove duplicates
    this.report().forests.forEach((forest: any) => {
      this.rangerDistricts.push(...forest.ranger_districts.filter((ranger_district: any) => ranger_district?.ranger_district != null).map((ranger_district: any) => ranger_district?.ranger_district));
    })
    this.rangerDistricts = [...new Set(this.rangerDistricts)]; //remove duplicates
    this.buildTableForms();

    this.form.patchValue({
      baer_name: this.report().baer_name,
      fire_number: this.report().IRWIN_unique_fire_identifier,
      job_code: this.report().job_code ?? "",
      fire_discovery_date: this.report().fire_discovery_date,
      containment_date_est: this.report().containment_date_est,
      containment_date: this.report().containment_date,
      percent_contained: this.report().percent_contained,

      vegetation_types: this.customATOB(this.report().vegetation_types ?? ""),
      dominant_soils: this.customATOB(this.report().dominant_soils ?? ""),
      geologic_types: this.customATOB(this.report().geologic_types ?? ""),
      other_roads: this.customATOB(this.report().other_roads ?? ""),
    })
    this.listenForChanges(this.form);
    this.formReady = true;
    this.loaded.emit(true);
  }

  buildTableForms(): void {

    let states = this.report().stateCounties.filter((state: StateCounty) => state?.state_name != null).map((state: StateCounty) => state?.state_name)
    states = [...new Set(states)]; //remove duplicates

    let burnData = [
      {
        ownership: "Private",
        acres_burned: 0,
        percent_burned: 0,
        report_soil_burn_severity_id: null
      },
      {
        ownership: "Other Federal",
        acres_burned: 0,
        percent_burned: 0,
        report_soil_burn_severity_id: null
      }
    ]

    for (let state of states) {
      burnData.unshift(
        {
          ownership: "State of " + state,
          acres_burned: 0,
          percent_burned: 0,
          report_soil_burn_severity_id: null
        }
      )
    }

    for (let forest of this.report().forests) {
      burnData.unshift(
        {
          ownership: forest.forest_name,
          acres_burned: 0,
          percent_burned: 0,
          report_soil_burn_severity_id: null
        }
      )
    }

    for (let item of this.report().soil_burn_severities) { //load existing data
      let burnDataItem = burnData.find((x: any) => x.ownership == item.ownership)
      if (burnDataItem) {
        burnDataItem.acres_burned = item.acres_burned;
        burnDataItem.percent_burned = item.percent_burned;
        burnDataItem.report_soil_burn_severity_id = item.report_soil_burn_severity_id;
      }
    }

    for (let data of burnData) {
      if (data.report_soil_burn_severity_id == null) {
        this.reportService.createBurnSeverity({
          report_id: this.report().report_id,
          ownership: data.ownership,
          acres_burned: 0,
          percent_burned: 0,
          burn_acres_developed: 0,
          burn_acres_unburned: 0,
          burn_acres_low: 0,
          burn_acres_moderate: 0,
          burn_acres_high: 0
        }).subscribe((response: any) => {
          this.acresBurned.push(this.formBuilder.group({
            name: data.ownership,
            acresBurned: data.acres_burned,
            percentBurned: data.percent_burned,
            report_soil_burn_severity_id: response.report_soil_burn_severity_id
          }))
        })
      }
      else {
        this.acresBurned.push(this.formBuilder.group({
          name: data.ownership,
          acresBurned: data.acres_burned,
          percentBurned: data.percent_burned,
          report_soil_burn_severity_id: data.report_soil_burn_severity_id
        }))
      }
    }

    this.report().watersheds.forEach((watershed: Watershed) => {
      this.subwatershed.push(this.formBuilder.group({
        HU12: watershed.huc12,
        name: watershed.huc12_name,
        totalAcres: watershed.acres_total,
        acresBurned: watershed.acres_burned,
        percentBurned: watershed.percent_burned,
        watershed_id: watershed.watershed_id,
      }))
    })

for (let forest of this.forests) {
  let questions1: any = {
    forest_id: forest.forest_id
  }
  for (let stream of this.streamTypes) {
    questions1[stream] = [(forest as any)[this.fieldDict[stream]] ?? 'N/A'];
  }
  this.streams.push(this.formBuilder.group(questions1))
  let questions2: any = { "other_roads": (forest as any)[this.fieldDict["other_roads"]] ?? 'N/A' }
  for (let level of this.maintenanceLevels) {
    questions2[level] = [(forest as any)[this.fieldDict[level]] ?? 'N/A'];
  }
  this.maintenance.push(this.formBuilder.group(questions2))
  let questions3: any = {}
  for (let trail of this.trailTypes) {
    questions3[trail] = [(forest as any)[this.fieldDict[trail]] ?? 'N/A'];
  }
  this.trails.push(this.formBuilder.group(questions3))
}
  }

ngOnChanges(): void {
  if(this.nav() != '') this.navigate();
}

navigate() {
  this.save(true);
}

route() {
  switch (this.nav()) {
    case 'forward': {
      this.router.navigate(['/2500-8-report'], { queryParams: { report_id: this.report().report_id, report_page_input: 3 } });
      break;
    }
    case 'back': {
      this.router.navigate(['/2500-8-report'], { queryParams: { report_id: this.report().report_id, report_page_input: 1 } });
      break;
    }
    case 'close': {
      this.router.navigate(['fire-overview'], { queryParams: { assessment_id: this.report().assessment_id } });
      break;
    }
    default: {
      if (Number(this.nav()))
        this.router.navigate(['/2500-8-report'], { queryParams: { report_id: this.report().report_id, report_page_input: this.nav() } });
      break;
    }
  }
}

onChangeEditor(html: any, field: any) {
  if (html == '<p></p>') {
    let patch = '{"' + field + '": ""}'
    this.form.patchValue(JSON.parse(patch))
  }
}

gatherData(): any {
  let data: any = {
    baer_name: this.form.value.baer_name as string | null,
    IRWIN_unique_fire_identifier: this.form.value.fire_number as string | null,
    job_code: this.form.value.job_code as string | null,

    fire_discovery_date: this.form.value.fire_discovery_date ? this.formatDate(new Date(this.form.value.fire_discovery_date)) : null,
    containment_date_est: this.form.value.containment_date_est ? this.formatDate(new Date(this.form.value.containment_date_est)) : null,
    containment_date: this.form.value.containment_date ? this.formatDate(new Date(this.form.value.containment_date)) : null,
    percent_contained: this.form.value.percent_contained ? Number(this.form.value.percent_contained) : null,

    vegetation_types: this.form.controls.vegetation_types.valid ? this.customBTOA(this.form.value.vegetation_types as string) : null,
    dominant_soils: this.form.controls.dominant_soils.valid ? this.customBTOA(this.form.value.dominant_soils as string) : null,
    geologic_types: this.form.controls.geologic_types.valid ? this.customBTOA(this.form.value.geologic_types as string) : null,
    other_roads: this.form.controls.other_roads.valid ? this.customBTOA(this.form.value.other_roads as string) : null,
  };
  let allFieldsFilled = true
  let optionalFields = ["other_roads"]
  if (this.optional() || this.simple()) optionalFields = ["other_roads", "vegetation_types", "dominant_soils", "geologic_types", "other_roads"]

  for (let field in this.form.controls) {
    if (optionalFields.includes(field)) continue
    if (this.form.get(field)?.value === null || this.form.get(field)?.value === "") {
      allFieldsFilled = false
    }
  }

  let watersheds: Watershed[] = this.subwatershed.controls.map((watershed: any) => {
    return {
      huc12: watershed.value.HU12,
      huc12_name: watershed.value.name,
      acres_total: watershed.value.totalAcres,
      acres_burned: watershed.value.acresBurned,
      percent_burned: watershed.value.percentBurned,
      watershed_id: watershed.value.watershed_id,
      report_id: this.report().report_id,
    } as Watershed
  });

  let forests: ReportForest[] = []
  for (let index = 0; index < this.streams.length; index++) {
    forests.push({
      forest_id: (this.streams.controls[index] as any).value["forest_id"],
      stream_miles_perennial: this.naFilter.transform((this.streams.controls[index] as any).value["Perennial"]),
      stream_miles_intermittent: this.naFilter.transform((this.streams.controls[index] as any).value["Intermittent"]),
      stream_miles_ephemeral: this.naFilter.transform((this.streams.controls[index] as any).value["Ephemeral"]),
      stream_miles_canal: this.naFilter.transform((this.streams.controls[index] as any).value["Canal/Ditch/Pipeline"]),
      roads_miles_basic: this.naFilter.transform((this.maintenance.controls[index] as any).value["1 - Basic Custodial Care (Closed)"]),
      roads_miles_clearance: this.naFilter.transform((this.maintenance.controls[index] as any).value["2 - High Clearance Vehicles"]),
      roads_miles_passenger: this.naFilter.transform((this.maintenance.controls[index] as any).value["3 - Suitable For Passenger Cars"]),
      roads_miles_moderate: this.naFilter.transform((this.maintenance.controls[index] as any).value["4 - Moderate Degree of User Comfort"]),
      roads_miles_high: this.naFilter.transform((this.maintenance.controls[index] as any).value["5 - High Degree of User Comfort"]),
      roads_miles_unknown: this.naFilter.transform((this.maintenance.controls[index] as any).value["Unknown or Decommissioned"]),
      transport_miles_motorized: this.naFilter.transform((this.trails.controls[index] as any).value["Motorized"]),
      transport_miles_non: this.naFilter.transform((this.trails.controls[index] as any).value["Non-motorized"]),
      transport_miles_snow: this.naFilter.transform((this.trails.controls[index] as any).value["Snow Trails"]),
      other_roads: this.naFilter.transform((this.maintenance.controls[index] as any).value["other_roads"]),
    })
  }

  let soils = this.acresBurned.value.map((item: any) => {
    let data = {
      report_id: this.report().report_id,
      report_soil_burn_severity_id: item.report_soil_burn_severity_id,
      ownership: item.name,
      acres_burned: this.naFilter.transform(item.acresBurned),
      percent_burned: +item.percentBurned,
    }
    return data
  })

  data.allFieldsFilled = allFieldsFilled
  data.watersheds = watersheds
  data.forests = forests
  data.soils = soils

  return data
}

  override save(navigate?: boolean) {
    this.navDebounceCheck(navigate)
    if (!this.form.dirty) {
      if (navigate) {
        this.route()
      }
      // Don't save if nothing has changed on autosave
      return
    }
    let data = this.gatherData();

  if (!navigate && this.checkIfNotNeedToSave(data)) return

  this.saveSubject.next(null)
  this.status.emit(data.allFieldsFilled ? 2 : 1);

  this.reportService.updateReport({
    report_id: this.report().report_id,
    baer_name: data.baer_name,
    IRWIN_unique_fire_identifier: data.IRWIN_unique_fire_identifier,
    job_code: data.job_code,

    fire_discovery_date: data.fire_discovery_date,
    containment_date_est: data.containment_date_est,
    containment_date: data.containment_date,
    percent_contained: data.percent_contained,

    vegetation_types: data.vegetation_types,
    dominant_soils: data.dominant_soils,
    geologic_types: data.geologic_types,
    other_roads: data.other_roads,

    watersheds: data.watersheds,
    forests: data.forests,
    soil_burn_severities: data.soils,
    page_2_status_id: data.allFieldsFilled ? 2 : 1,
  }).subscribe({
    next: (response) => {
      if (navigate) {
        if (this.nav() == "forward" && !data.allFieldsFilled) {
          this.form.markAllAsTouched();
          this.errorList = [];
          for (let field in this.form.controls) {
            const control = this.form.get(field);
            if (control?.invalid) {
              this.errorList.push(field.replaceAll('_', ' '));
            }
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
          this.nav.set('');
          this.postSave();
        } else
          this.route(); // navigate route after save because next page needs to see this page's changes
      } else {
        this.postSave();
      }
    },
    error: (response) => {
      this.toast.openErrorSnackBar(`There was an error saving the report`, "Please try again.");
    },
    complete: () => {
      this.form.markAsPristine();
      if (navigate) this.navDebounce = false
    }
  })
}

save_close() {
  this.save();
  this.router.navigate(['fire-overview'], { queryParams: { assessment_id: this.report().assessment_id } });
}

sumRoadMiles(i: number) {
  let values = (this.form.value.maintenance as any)[i]
  values["other_roads"] = 0
  let total = 0
  for (let key in values) {
    total += +(this.naFilter.transform(values[key]) ?? 0)
  }
  return total
}

sumStream(stream: string) {
  let total = 0
  this.form.value.streams?.forEach((item: any) => {
    total += +(this.naFilter.transform(item[stream]) ?? 0)
  })
  return total
}

sumTrail(trail: string) {
  let total = 0
  this.form.value.trails?.forEach((item: any) => {
    total += +(this.naFilter.transform(item[trail]) ?? 0)
  })
  return total
}

sumAcresBurned() {
  let total = 0
  this.form.value.acresBurned?.forEach((item: any) => {
    total += +(item.acresBurned ?? 0)
  })
  return total
}

addWaterShed() {
  this.reportService.createWaterShed(this.report().report_id).subscribe((watershed: any) => {
    this.subwatershed.push(this.formBuilder.group({
      HU12: "",
      name: "",
      totalAcres: null,
      acresBurned: null,
      percentBurned: null,
      watershed_id: watershed.watershed_id
    }))
  })
}

formatDate(date: Date) {
  let year = date.getFullYear();

  let month = (1 + date.getMonth()).toString();
  month = month.length > 1 ? month : '0' + month;

  let day = date.getDate().toString();
  day = day.length > 1 ? day : '0' + day;

  return month + '/' + day + '/' + year;
}

removeSubwatershed(index: number) {
  if (this.subwatershed.controls[index].value.watershed_id == undefined) {
    this.subwatershed.removeAt(index) // watershed not in database yet, no need for api call
    return
  }
  let control = this.subwatershed.controls[index]
  this.reportService.deleteWatershed(this.subwatershed.controls[index].value.watershed_id).subscribe({
    next: (response) => {
    },
    error: (response) => {
      this.subwatershed.insert(index, control) // re-add watershed row to show delete was unsuccessful
      this.toast.openErrorSnackBar(`There was an error removing the subwatershed`, "Please try again.");
    },
  })
  this.subwatershed.removeAt(index) // remove row after sending api call for immediate feedback
}

customATOB(s: string): string {
  try {
    return atob(s)
  } catch (err) {
    if (err) {
      return s
    }
  }
  return s
}

customBTOA(s: string): string {
  return btoa(s.replace(/[\u00A0-\u2666]/g, function (c) {
    return '&#' + c.charCodeAt(0) + ';';
  }));
}
}
